import React from 'react'
import {
  Box,
  SimpleGrid,
  Icon,
  Text,
  Stack,
  Flex,
  Button,
  Container,
  Heading,
  Badge,
  Link as ChakraLink
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { FaYoutube, FaChartLine, FaExternalLinkAlt } from 'react-icons/fa'

const MotionBox = motion(Box)

interface ResourceCardProps {
  title: string
  desc: string
  href: string
  icon: React.ElementType
  colorFrom: string
  colorTo: string
  cta: string
  delay: number
}

const ResourceCard = ({ title, desc, href, icon, colorFrom, colorTo, cta, delay }: ResourceCardProps) => {
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      bg="dark.card"
      p={8}
      borderRadius="2xl"
      border="1px solid"
      borderColor="whiteAlpha.100"
      position="relative"
      overflow="hidden"
      _hover={{
        transform: 'translateY(-8px)',
        boxShadow: `0 20px 40px -15px ${colorFrom}40`,
        borderColor: 'whiteAlpha.300'
      }}
      transitionProperty="transform, box-shadow, border-color"
      transitionDuration="0.3s"
    >
      {/* Gradient Background Overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        w="100%"
        h="100%"
        bgGradient={`linear(to-br, ${colorFrom}10, ${colorTo}10)`}
        opacity={0.5}
      />

      <Stack spacing={6} position="relative" zIndex={1}>
        <Flex
          w={14}
          h={14}
          align="center"
          justify="center"
          color="white"
          rounded="2xl"
          bgGradient={`linear(to-br, ${colorFrom}, ${colorTo})`}
          boxShadow="lg"
        >
          <Icon as={icon} w={7} h={7} />
        </Flex>
        
        <Box>
          <Heading size="md" color="white" mb={3}>
            {title}
          </Heading>
          <Text color="gray.400" fontSize="md" lineHeight="relaxed" mb={6}>
            {desc}
          </Text>
          
          <Button
            as={ChakraLink}
            href={href}
            isExternal
            rightIcon={<FaExternalLinkAlt />}
            variant="outline"
            color="white"
            borderColor="whiteAlpha.300"
            _hover={{
              bg: 'whiteAlpha.100',
              borderColor: 'whiteAlpha.500'
            }}
            size="sm"
          >
            {cta}
          </Button>
        </Box>
      </Stack>
    </MotionBox>
  )
}

const ResourceSection = () => {
  const { t } = useTranslation()

  return (
    <Box py={{ base: 20, md: 24 }} bg="dark.bg">
      <Container maxW="6xl">
        <Stack spacing={4} textAlign="center" mb={16}>
          <Box>
            <Badge
              colorScheme="blue"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="sm"
              mb={4}
            >
              {t('학습 & 시그널')}
            </Badge>
          </Box>
          <Heading
            size="2xl"
            bgGradient="linear(to-r, white, gray.400)"
            bgClip="text"
          >
            {t('실시간 시그널과 인사이트')}
          </Heading>
          <Text color="gray.400" fontSize="lg" maxW="2xl" mx="auto">
            {t('홈에서 바로 트레이딩뷰 시그널 보조지표와 XRP 전문 유튜브 채널을 확인하세요.')}
          </Text>
        </Stack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <ResourceCard
            title={t('트레이딩뷰 시그널 보조지표')}
            desc={t('전략 설계에 도움 되는 커뮤니티 스크립트 페이지로 이동합니다. 다양한 보조지표를 활용해 나만의 전략을 만들어보세요.')}
            href="https://kr.tradingview.com/script/dUkRohgK/"
            icon={FaChartLine}
            colorFrom="#4F46E5"
            colorTo="#9333EA"
            cta={t('트레이딩뷰 열기')}
            delay={0}
          />
          <ResourceCard
            title={t('리스타트 피플 – XRP 유튜브')}
            desc={t('XRP 중심의 시황, 매크로, 전략 인사이트를 영상으로 빠르게 학습하세요. 전문가의 분석을 통해 시장의 흐름을 읽을 수 있습니다.')}
            href="https://www.youtube.com/@%EB%A6%AC%EC%8A%A4%ED%83%80%ED%8A%B8%ED%94%BC%ED%94%8C"
            icon={FaYoutube}
            colorFrom="#DC2626"
            colorTo="#F59E0B"
            cta={t('채널 보기')}
            delay={0.2}
          />
        </SimpleGrid>
      </Container>
    </Box>
  )
}

export default ResourceSection
